import React, { useState, useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import AppBar from '@mui/material/AppBar'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import { Box, Tooltip } from '@mui/material'
import { useStyles } from '../Pathologist/inboxStyles'
import { TabPanelProps } from '../../../interfaces/inbox'
import TableComponent from '../Common/TableComponent'
import { UnassignedColumns, AssignedColumns, LMComplete } from './tableColumnHeaders'
import { getUnassignedData, getLabManagerCompleteData, getAssignedData } from '../../../actions'
import { RootState } from '../../../reducers'
import { Clear as ClearIcon } from '@mui/icons-material'
import { LoadBalancer } from './../../HOC/LoadBalancer'

function a11yProps(index: unknown) {
  return {
    id: `case-worklist-${index}`,
    'aria-controls': `case-worklist-${index}`,
  }
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`case-worklist-${index}`}
      aria-labelledby={`case-worklist-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={0} pt={1}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

export function LabManager({ user }): JSX.Element {
  const classes = useStyles()
  const [value, setValue] = useState(0)
  const dispatch = useDispatch()
  const UnassignedData = useSelector((state: RootState) => state.getUnassignedData)
  const AssignedData = useSelector((state: RootState) => state.getAssignedData)
  const LMCompleteData = useSelector((state: RootState) => state.getLabManagerCompleteData)
  const { display_labels } = useSelector((state: RootState) => state.appConfig)
  const [loadBalancer, setLoadBalancer] = useState(true)
  const procedureData = useSelector((state: RootState) => state.getFilterProcedureCodeData)
  const [onTrfSearch, setOnTrfSearch] = useState(false)
  const [tabChanged, setTabChanged] = useState(0)
  const gridPageSize = 10

  useEffect(() => {
    if (value === 0) {
      getUnassignedData({
        id: `${user}`,
        pageIndex: 1,
        pageSize: gridPageSize,
        filterField: 'created_datetime',
        filterValue: null,
        caseView: 'unassigned',
        startDate: null,
        endDate: null,
        sortOrder: 'asc',
        sortField: 'case_number',
      })
    }
    if (value === 1) {
      dispatch(
        getAssignedData({
          id: `${user}`,
          pageIndex: 1,
          pageSize: gridPageSize,
          filterField: 'created_datetime',
          filterValue: null,
          caseView: 'assigned',
          startDate: null,
          endDate: null,
          sortOrder: 'asc',
          sortField: 'case_number',
        }),
      )
    }
    if (value === 2) {
      dispatch(
        getLabManagerCompleteData({
          id: `${user}`,
          pageIndex: 1,
          pageSize: gridPageSize,
          filterField: 'updated_datetime',
          filterValue: null,
          caseView: 'completed',
          startDate: null,
          endDate: null,
          sortOrder: 'desc',
          sortField: 'case_number',
        }),
      )
    }
  }, [dispatch, user, value])

  const handleChange = (event, newValue: number) => {
    if (newValue === undefined) return
    setValue(newValue)
  }

  function enableLoadBalancer() {
    setLoadBalancer(true)
  }

  function disableLoadBalancer() {
    setLoadBalancer(false)
  }

  const ProcedureCodeRenderer = {
    field: 'procedure_code',
    headerName: 'Case Type',
    sortable: true,
    width: 200,
    headerClassName: 'super-app-theme--header',
    disableClickEventBubbling: true,
    valueGetter: (params) => {
      const filterUserResults = procedureData.procedureCodeMap.filter((value) => {
        return value.id == params.row.procedure_code
      })
      return filterUserResults[0]?.display
    },
  }

  if (display_labels) {
    AssignedColumns.push(ProcedureCodeRenderer)
    UnassignedColumns.push(ProcedureCodeRenderer)
    LMComplete.push(ProcedureCodeRenderer)
  }

  const onSearch = useCallback(
    (val) => {
      setOnTrfSearch(true)
      setTabChanged(value)
      handleChange(null, 5)
      setLoadBalancer(false)
    },
    [onTrfSearch, value, tabChanged],
  )

  function clearSearch() {
    handleChange(null, tabChanged)
    setLoadBalancer(true)
  }

  return (
    <div>
      <Box>
        <AppBar position="static" className={classes.appBar}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="case-worklist"
            classes={{
              indicator: classes.indicator,
            }}
            style={{ width: 'min-content' }}
          >
            <Tab label="Unassigned " onClick={enableLoadBalancer} {...a11yProps(1)} style={{ margin: '0px 10px' }} />
            <Tab label="Assigned" onClick={disableLoadBalancer} {...a11yProps(0)} style={{ margin: '0px 10px' }} />
            <Tab label="Completed " onClick={disableLoadBalancer} {...a11yProps(1)} style={{ margin: '0px 10px' }} />
            {onTrfSearch && (
              <Tab label="" {...a11yProps(4)} style={{ margin: '0px', minWidth: '0px', padding: '0px' }} />
            )}
          </Tabs>
          <div className={classes.loadBalancer} style={{ marginTop: '10px' }}>
            {loadBalancer && <LoadBalancer user={user} />}
            {/* <SearchTRFInput user={user} role={role} onSearch={onSearch} /> */}
          </div>
        </AppBar>
        {/* {value > 0 && value !== 5 && <UserBanner updateUserView={updateUserView} userInfo={userInfo} />} */}
        <TabPanel value={value} index={0}>
          <TableComponent
            userId={user}
            caseView={'unassigned'}
            isSearchTable={false}
            reducerData={UnassignedData}
            dispatchData={getUnassignedData}
            tableColumns={UnassignedColumns}
          />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <TableComponent
            userId={user}
            caseView={'assigned'}
            isSearchTable={false}
            reducerData={AssignedData}
            dispatchData={getAssignedData}
            tableColumns={AssignedColumns}
          />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <TableComponent
            userId={user}
            caseView={'completed'}
            isSearchTable={false}
            reducerData={LMCompleteData}
            dispatchData={getLabManagerCompleteData}
            tableColumns={LMComplete}
          />
        </TabPanel>
      </Box>
    </div>
  )
}
